import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, AUTH_STATE } from "../contexts/AuthContext";

/**
 * Fase 1F/1L.8C — Tela de seleção de empresa (modo multi-tenant).
 * - Mostra nome + slug (nome como texto primário, slug secundário).
 * - Bloqueio contra clique duplicado.
 * - Envio usa exclusivamente ``tenant_id``.
 * - Erros genéricos.
 * - Opção de voltar ao login que chama logout global.
 * - Quando ``admin_console_available === true`` (Fase 1L.8C), exibe
 *   CTA "Administração global" acima da lista de empresas.
 */
export default function PlatformSelectTenant() {
  const navigate = useNavigate();
  const {
    state, pendingTenants, selectTenant, cancelTenantSelection,
    adminConsoleAvailable, enterAdminConsole,
  } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Se não há seleção pendente (ex.: acesso direto), volta ao login.
  useEffect(() => {
    if (state !== AUTH_STATE.NEEDS_TENANT) {
      if (state === AUTH_STATE.AUTHED) {
        navigate("/dashboard", { replace: true });
      } else if (state === AUTH_STATE.AUTHED_ADMIN_ONLY) {
        navigate("/administracao-global", { replace: true });
      } else if (state === AUTH_STATE.GUEST) {
        navigate("/login", { replace: true });
      }
    }
  }, [state, navigate]);

  const onSelect = async (tenantId) => {
    if (busy) return;
    setBusy(true);
    setErr("");
    try {
      await selectTenant(tenantId);
      toast.success("Empresa selecionada.");
      navigate("/dashboard", { replace: true });
    } catch {
      setErr("Não foi possível selecionar essa empresa. Tente novamente.");
    } finally {
      setBusy(false);
    }
  };

  const onBack = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await cancelTenantSelection();
    } finally {
      setBusy(false);
      navigate("/login", { replace: true });
    }
  };

  const onAdminConsole = async () => {
    if (busy) return;
    setBusy(true);
    setErr("");
    try {
      await enterAdminConsole();
      navigate("/administracao-global", { replace: true });
    } catch {
      setErr("Não foi possível acessar a Administração global. Tente novamente.");
    } finally {
      setBusy(false);
    }
  };

  const tenants = Array.isArray(pendingTenants) ? pendingTenants : [];
  const count = tenants.length;

  // Textos dinâmicos (Fase 1L.8C).
  let title = "Selecione uma empresa";
  let helper = "Escolha a empresa em que deseja entrar.";
  if (count === 0 && adminConsoleAvailable) {
    title = "Administração global";
    helper = "Acesse o painel de administração da plataforma.";
  } else if (count >= 2) {
    helper =
      "Você faz parte de mais de uma empresa. Escolha em qual deseja entrar agora.";
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-lg bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-slate-900 grid place-items-center text-white font-heading font-bold">R</div>
          <span className="font-heading text-lg font-semibold tracking-tight">Ruma Flow</span>
        </div>
        <h1
          className="font-heading text-2xl font-semibold tracking-tight text-slate-900"
          data-testid="select-tenant-title"
        >
          {title}
        </h1>
        <p className="text-sm text-slate-500 mt-1" data-testid="select-tenant-helper">
          {helper}
        </p>

        {err && (
          <div role="alert" data-testid="select-tenant-error"
               className="mt-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {err}
          </div>
        )}

        {adminConsoleAvailable && (
          <button
            type="button"
            onClick={onAdminConsole}
            disabled={busy}
            data-testid="select-tenant-admin-console-cta"
            className="mt-6 w-full text-left border border-slate-900 rounded-lg px-4 py-3 hover:bg-slate-900 hover:text-white transition disabled:opacity-50"
          >
            <div className="font-medium">Administração global</div>
            <div className="text-xs text-slate-500 mt-0.5 group-hover:text-white/80">
              Painel de administração da plataforma
            </div>
          </button>
        )}

        {count > 0 && (
          <ul className="mt-6 space-y-2" data-testid="tenant-list">
            {tenants.map((t) => (
              <li key={t.tenant_id}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onSelect(t.tenant_id)}
                  data-testid={`select-tenant-item-${t.slug}`}
                  className="w-full text-left border border-slate-200 rounded-lg px-4 py-3 hover:border-slate-900 hover:bg-slate-50 transition disabled:opacity-50"
                >
                  <div className="font-medium text-slate-900">{t.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{t.slug}</div>
                </button>
              </li>
            ))}
          </ul>
        )}

        {count === 0 && !adminConsoleAvailable && (
          <div
            className="mt-6 text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg px-4 py-6 text-center"
            data-testid="select-tenant-empty"
          >
            Nenhuma empresa disponível para este acesso.
          </div>
        )}

        <button
          type="button"
          onClick={onBack}
          disabled={busy}
          data-testid="select-tenant-back"
          className="mt-6 w-full h-10 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Voltar ao login
        </button>
      </div>
    </div>
  );
}
